import React, { memo, useCallback } from 'react';

import { Box, Button, Typography } from '@mui/material';
import CheckboxCDS from '@sharedCR/coreDesignSystem/form/CheckboxCDS';
import PropTypes from 'prop-types';

import { AiProcessSubjectRequirement } from 'constants/aiProcessRuns';

import {
  START_BUTTON_LABEL,
  STARTING_BUTTON_LABEL,
  SUBJECT_STEP_BACK_LABEL,
  SUBJECT_STEP_EMPTY_MESSAGE,
  SUBJECT_STEP_LOADING_MESSAGE,
  SUBJECT_STEP_OPTIONAL_SUBTITLE,
  SUBJECT_STEP_REQUIRED_SUBTITLE,
} from '../StartAiProcessDrawer.constants';
import { StartAiProcessDrawerTestIds } from '../StartAiProcessDrawer.testIds';

import {
  containerStyles,
  emptyStateStyles,
  footerRowStyles,
  headerRowStyles,
  listStyles,
  subtitleStyles,
} from './CertificationSubjectStep.styles';
import { buildCertificationOptionLabel } from './CertificationSubjectStep.utils';

const CertificationSubjectStep = ({
  process,
  requirement,
  certifications,
  isLoading,
  selectedCertIds,
  isStarting,
  onToggleCert,
  onBack,
  onStart,
}) => {
  const isRequired = requirement === AiProcessSubjectRequirement.REQUIRED_CERTIFICATION;
  const canStart = !isStarting && (!isRequired || selectedCertIds.length > 0);

  const onStartClick = useCallback(() => {
    onStart(selectedCertIds);
  }, [onStart, selectedCertIds]);

  // Stable handler reads the cert id off the checkbox `name` so each row doesn't
  // allocate a new closure (react-perf/jsx-no-new-function-as-prop).
  const onCertChange = useCallback(
    event => {
      onToggleCert(event.target.name);
    },
    [onToggleCert],
  );

  return (
    <Box data-testid={StartAiProcessDrawerTestIds.SUBJECT_STEP} sx={containerStyles}>
      <Box sx={headerRowStyles}>
        <Typography variant="medium16">{process.name}</Typography>
        <Typography sx={subtitleStyles} variant="regular14">
          {isRequired ? SUBJECT_STEP_REQUIRED_SUBTITLE : SUBJECT_STEP_OPTIONAL_SUBTITLE}
        </Typography>
      </Box>

      {isLoading && (
        <Box sx={emptyStateStyles}>
          <Typography variant="regular14">{SUBJECT_STEP_LOADING_MESSAGE}</Typography>
        </Box>
      )}

      {!isLoading && certifications.length === 0 && (
        <Box sx={emptyStateStyles}>
          <Typography variant="regular14">{SUBJECT_STEP_EMPTY_MESSAGE}</Typography>
        </Box>
      )}

      {!isLoading && certifications.length > 0 && (
        <Box sx={listStyles}>
          {certifications.map(certification => (
            <CheckboxCDS
              data-testid={StartAiProcessDrawerTestIds.SUBJECT_CERT_OPTION}
              key={certification.id}
              label={buildCertificationOptionLabel(certification)}
              name={certification.id}
              value={selectedCertIds.includes(certification.id)}
              onChange={onCertChange}
            />
          ))}
        </Box>
      )}

      <Box sx={footerRowStyles}>
        <Button
          data-testid={StartAiProcessDrawerTestIds.SUBJECT_BACK_BUTTON}
          size="small"
          variant="text"
          onClick={onBack}
        >
          {SUBJECT_STEP_BACK_LABEL}
        </Button>
        <Button
          data-testid={StartAiProcessDrawerTestIds.SUBJECT_START_BUTTON}
          disabled={!canStart}
          size="small"
          variant="contained"
          onClick={onStartClick}
        >
          {isStarting ? STARTING_BUTTON_LABEL : START_BUTTON_LABEL}
        </Button>
      </Box>
    </Box>
  );
};

CertificationSubjectStep.propTypes = {
  process: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired,
  requirement: PropTypes.string.isRequired,
  certifications: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      status: PropTypes.string,
      submittedAt: PropTypes.string,
      approvedAt: PropTypes.string,
      expiresAt: PropTypes.string,
    }),
  ).isRequired,
  selectedCertIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  onToggleCert: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onStart: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  isStarting: PropTypes.bool,
};

export default memo(CertificationSubjectStep);
